import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Check, MapPin, User } from 'lucide-react';
import { useCheckout } from 'context/checkout/Checkout';
import currency from 'helpers/currency';
import {
    coveredSessionIds,
    formatDay,
    groupSessionsByDay,
    isTicketAvailable,
    kindLabel,
    ticketProducts,
} from 'helpers/eventSessions';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import fonts from 'theme/fonts';

const FILTERS = [
    { key: 'all', label: 'Todas' },
    { key: 'talks', label: 'Palestras' },
    { key: 'workshops', label: 'Minicursos' },
    { key: 'mine', label: 'Liberadas no meu pedido' },
];

const OK_TEXT = '#0a7a35';

function priceLabel(product) {
    const price = Number(product?.price);

    return price > 0 ? currency.format(price) : 'Grátis';
}

export default function SchedulePanel({ event }) {
    const { checkout } = useCheckout();
    const days = useMemo(() => groupSessionsByDay(event?.sessions), [event?.sessions]);
    const tickets = useMemo(() => ticketProducts(event), [event]);
    const covered = useMemo(() => coveredSessionIds(event, checkout?.batches), [event, checkout]);

    const [day, setDay] = useState(days[0]?.date);
    const [filter, setFilter] = useState('all');

    const hasCovered = covered.size > 0;
    const activeFilter = filter === 'mine' && !hasCovered ? 'all' : filter;
    const current = days.find(item => item.date === day) || days[0];

    const sessions = (current?.items || []).filter(session => {
        if (activeFilter === 'talks') return session.kind !== 'workshop';
        if (activeFilter === 'workshops') return session.kind === 'workshop';
        if (activeFilter === 'mine') return covered.has(session.id);
        return true;
    });

    function ticketsFor(session) {
        return tickets.filter(ticket => session.product_ids?.includes(ticket.id));
    }

    return (
        <Wrapper>
            <Heading>Programação</Heading>

            <Days>
                {days.map(item => {
                    const { weekday, label } = formatDay(item.date);
                    const unlocked = item.items.some(session => covered.has(session.id));

                    return (
                        <DayButton
                            key={item.date}
                            type='button'
                            aria-pressed={current?.date === item.date}
                            $active={current?.date === item.date}
                            onClick={() => setDay(item.date)}
                        >
                            {unlocked && <Dot title='Tem sessão liberada pelo seu pedido' />}
                            <small>{weekday}</small>
                            <b>{label}</b>
                            <em>{item.items.length} {item.items.length === 1 ? 'sessão' : 'sessões'}</em>
                        </DayButton>
                    );
                })}
            </Days>

            <Filters>
                {FILTERS.map(item => (
                    <Chip
                        key={item.key}
                        type='button'
                        aria-pressed={activeFilter === item.key}
                        $active={activeFilter === item.key}
                        disabled={item.key === 'mine' && !hasCovered}
                        onClick={() => setFilter(item.key)}
                    >
                        {item.label}
                    </Chip>
                ))}
            </Filters>

            {sessions.length === 0 && (
                <Empty>Nenhuma sessão neste dia com esse filtro.</Empty>
            )}

            {sessions.map(session => {
                const included = covered.has(session.id);
                const options = ticketsFor(session);
                const available = options.filter(isTicketAvailable);

                return (
                    <Session key={session.id} $included={included}>
                        <Time>
                            <b>{session.start}</b>
                            {session.end && <span>até {session.end}</span>}
                        </Time>

                        <Body>
                            <Meta>
                                <Kind $kind={session.kind}>{kindLabel(session.kind)}</Kind>
                                {session.location && (
                                    <Place><MapPin size={13} /> {session.location}</Place>
                                )}
                            </Meta>
                            <SessionTitle>{session.title}</SessionTitle>
                            {session.speaker && (
                                <Speaker><User size={14} /> {session.speaker}</Speaker>
                            )}
                            {session.description && <Description>{session.description}</Description>}
                        </Body>

                        <Access>
                            {included ? (
                                <Included><Check size={15} strokeWidth={3} /> Incluído no seu pedido</Included>
                            ) : session.open_access ? (
                                <>
                                    <AccessLabel>Como entrar</AccessLabel>
                                    <Option as='div'><span>Qualquer ingresso do evento</span></Option>
                                </>
                            ) : available.length > 0 ? (
                                <>
                                    <AccessLabel>Como entrar</AccessLabel>
                                    {available.map(ticket => (
                                        <Option as='div' key={ticket.id}>
                                            <span>{ticket.name}</span>
                                            <b>{priceLabel(ticket)}</b>
                                        </Option>
                                    ))}
                                </>
                            ) : (
                                <SoldOut>{options.length ? 'Esgotado' : 'Sem ingresso à venda'}</SoldOut>
                            )}

                            {!!session.capacity && session.kind === 'workshop' && (
                                <Capacity>{session.capacity} vagas</Capacity>
                            )}
                        </Access>
                    </Session>
                );
            })}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
`;

const Heading = styled.h2`
    margin: 0;
    font-size: ${fonts.sizes.title};
    font-weight: ${fonts.weight.bold};
    color: ${colors.occasionalPurple};
`;

const Days = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(72px, 1fr));
    gap: 8px;
`;

const Dot = styled.span`
    position: absolute;
    top: 6px;
    right: 6px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${colors.successDark};
`;

const DayButton = styled.button`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 9px 6px;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    border: 1px solid ${({ $active }) => ($active ? colors.occasionalPurple : colors.occasionalGrey)};
    background: ${({ $active }) => ($active ? colors.occasionalPurple : colors.neutralLight)};
    color: ${({ $active }) => ($active ? colors.neutralLight : colors.nightDark)};

    small {
        font-size: ${fonts.sizes.small};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        opacity: 0.8;
    }

    b {
        font-size: 17px;
        font-variant-numeric: tabular-nums;
    }

    em {
        font-style: normal;
        font-size: ${fonts.sizes.mini};
        opacity: 0.8;
    }
`;

const Filters = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const Chip = styled.button`
    border-radius: 999px;
    padding: 5px 14px;
    font-family: inherit;
    font-size: 13px;
    cursor: pointer;
    font-weight: ${({ $active }) => ($active ? 600 : 500)};
    border: 1px solid ${({ $active }) => ($active ? colors.occasionalPurple : colors.occasionalGrey)};
    background: ${({ $active }) => ($active ? `${colors.occasionalPurple}14` : colors.neutralLight)};
    color: ${({ $active }) => ($active ? colors.occasionalPurple : colors.nightDark)};

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const Empty = styled.p`
    margin: 0;
    color: ${colors.night};
`;

const Session = styled.article`
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    padding: 16px;
    background: ${colors.neutralLight};
    border-radius: 12px;
    border: 1px solid ${({ $included }) => ($included ? OK_TEXT : colors.occasionalGrey)};
    box-shadow: ${({ $included }) => ($included ? `inset 4px 0 0 ${OK_TEXT}` : 'none')};

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: 78px minmax(0, 1fr) 210px;
        gap: 16px;
    }
`;

const Time = styled.div`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
    color: ${colors.nightDark};

    b {
        font-size: 20px;
        line-height: 1.2;
        font-variant-numeric: tabular-nums;
    }

    span {
        font-size: ${fonts.sizes.small};
        color: ${colors.night};
        font-variant-numeric: tabular-nums;
    }

    @media screen and (min-width: ${breakpoints.md}) {
        flex-direction: column;
        gap: 0;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 12px;
    font-size: 13px;
    color: ${colors.night};
`;

const Kind = styled.span`
    border-radius: 6px;
    padding: 1px 8px;
    font-size: ${fonts.sizes.small};
    font-weight: 600;
    background: ${({ $kind }) => ($kind === 'workshop' ? colors.warning : `${colors.occasionalPurple}14`)};
    color: ${({ $kind }) => ($kind === 'workshop' ? '#7a4a00' : colors.occasionalPurple)};
`;

const Place = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const SessionTitle = styled.h3`
    margin: 6px 0 0;
    font-size: 17px;
    line-height: 1.3;
    font-weight: ${fonts.weight.bold};
    color: ${colors.nightDark};
`;

const Speaker = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 4px 0 0;
    font-size: ${fonts.sizes.default};
    color: ${colors.night};
`;

const Description = styled.p`
    margin: 8px 0 0;
    font-size: ${fonts.sizes.default};
    line-height: 1.5;
    color: ${colors.nightDark};
    white-space: pre-line;
`;

const Access = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
`;

const AccessLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Option = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 8px;
    align-items: baseline;
    padding: 6px 10px;
    border-radius: 8px;
    font-size: 13px;
    background: ${colors.neutral};
    border: 1px solid ${colors.occasionalGrey};
    color: ${colors.nightDark};

    b {
        font-weight: 600;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
    }
`;

const Included = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: ${fonts.sizes.default};
    font-weight: 600;
    color: ${OK_TEXT};
`;

const SoldOut = styled.span`
    padding: 2px 10px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    background: ${colors.danger}1a;
    color: ${colors.dangerDark};
`;

const Capacity = styled.span`
    font-size: ${fonts.sizes.small};
    color: ${colors.night};
`;
